// Generative per-map score. A lookahead step sequencer (16ths, or 12/8 triplets) drives a handful of PERSISTENT voices:
// a chord pad (detuned oscillator pairs that glide between chords), an optional drone, monophonic bass / arp / lead synths
// and a 4-piece drum kit, all with re-triggered envelopes, so the node count is fixed for the whole run and nothing is
// allocated per note. Each map's ScoreSpec (src/maps/<id>/audio.ts) sets the key, mode, tempo, chord loop, timbres,
// patterns and its own original motifs. Intensity (boss alive / overtime) switches to the 'hot' drum, bass and arp
// patterns and opens the pad filter. Form: 2 bars of pad + bass, then everything; the lead plays its motifs on chosen bars
// of every 8-bar phrase; bar 8 of a phrase plays the drum fill; bars 29-32 of every 32 are a breakdown (no kick/snare)
// unless the music is hot.
import { degreeNote, midiHz, type DrumPatterns, type InstSpec, type KitName, type ScoreSpec } from './mapAudioDef';
import { Kit, tc } from './kit';

class Mono {
  private o1: OscillatorNode; private o2: OscillatorNode | null = null;
  private m: OscillatorNode | null = null; private mg: GainNode | null = null;
  private f: BiquadFilterNode; private a: GainNode;
  constructor(kit: Kit, private s: InstSpec, out: AudioNode, send: AudioNode) {
    this.o1 = kit.osc(s.wave, 220); this.f = kit.filter('lowpass', s.cutoff, s.q ?? 0.9); this.a = kit.gain(0);
    const pre: AudioNode = s.drive && s.drive > 1 ? kit.shaper(s.drive) : this.f;
    this.o1.connect(pre);
    if (s.detune) { this.o2 = kit.osc(s.wave, 220, s.detune); this.o2.connect(pre); }
    if (pre !== this.f) pre.connect(this.f);
    if (s.fm || s.vibrato) {
      this.m = kit.osc('sine', s.vibrato && !s.fm ? s.vibrato.rate : 220); this.mg = kit.gain(0); this.m.connect(this.mg);
      this.mg.connect(this.o1.frequency); if (this.o2) this.mg.connect(this.o2.frequency);
    }
    this.f.connect(this.a).connect(out);
    if (s.send) { const g = kit.gain(s.send); this.a.connect(g).connect(send); }
  }
  note(t: number, midi: number, dur: number, vel: number): void {
    const s = this.s, hz = midiHz(midi), a = this.a.gain, pk = s.gain * vel;
    if (s.glide) { this.o1.frequency.setTargetAtTime(hz, t, s.glide); this.o2?.frequency.setTargetAtTime(hz, t, s.glide); }
    else { this.o1.frequency.setValueAtTime(hz, t); this.o2?.frequency.setValueAtTime(hz, t); }
    if (s.fm && this.m && this.mg) {
      this.m.frequency.setValueAtTime(hz * s.fm.ratio, t);
      this.mg.gain.cancelScheduledValues(t); this.mg.gain.setTargetAtTime(hz * s.fm.index * vel, t, 0.002); this.mg.gain.setTargetAtTime(hz * s.fm.index * 0.08, t + 0.004, tc(s.fm.decay));
    } else if (s.vibrato && this.mg) {
      const depth = hz * (Math.pow(2, s.vibrato.cents / 1200) - 1);
      this.mg.gain.cancelScheduledValues(t); this.mg.gain.setTargetAtTime(0, t, 0.01); if (dur > 0.25) this.mg.gain.setTargetAtTime(depth, t + 0.18, 0.15);
    }
    if (s.env) {
      const fp = this.f.frequency; fp.cancelScheduledValues(t);
      fp.setTargetAtTime(Math.min(16000, s.cutoff * (1 + s.env * vel)), t, 0.003); fp.setTargetAtTime(s.cutoff, t + s.atk + 0.005, tc(s.dec));
    }
    a.cancelScheduledValues(t);
    a.setTargetAtTime(pk, t, tc(s.atk)); a.setTargetAtTime(pk * s.sus, t + s.atk, tc(s.dec)); a.setTargetAtTime(0, t + Math.max(s.atk, dur), tc(s.rel));
  }
}

class Pad {
  private oscs: OscillatorNode[] = []; readonly f: BiquadFilterNode; readonly a: GainNode;
  constructor(kit: Kit, n: number, wave: OscillatorType, detune: number, cutoff: number, q: number, out: AudioNode) {
    this.f = kit.filter('lowpass', cutoff, q); this.a = kit.gain(0);
    for (let i = 0; i < n; i++) for (const d of [-detune, detune]) { const o = kit.osc(wave, 220, d); o.connect(this.f); this.oscs.push(o); }
    this.f.connect(this.a).connect(out);
  }
  chord(t: number, notes: ArrayLike<number>, n: number): void {
    for (let i = 0; i < n; i++) { const hz = midiHz(notes[i]); this.oscs[i * 2].frequency.setTargetAtTime(hz, t, 0.04); this.oscs[i * 2 + 1].frequency.setTargetAtTime(hz, t, 0.04); }
  }
}

interface KitVoice { hi: number; lo: number; pdec: number; dec: number; g: number }
interface KitDef { kick: KitVoice; tom: KitVoice; snare: { f: number; q: number; dec: number; body: number; bdec: number; g: number }; hat: { f: number; dec: number; open: number; g: number } }
const KITS: Record<KitName, KitDef> = {
  tight: { kick: { hi: 150, lo: 46, pdec: 0.045, dec: 0.32, g: 1 }, tom: { hi: 180, lo: 110, pdec: 0.08, dec: 0.25, g: 0.55 },
    snare: { f: 1900, q: 0.9, dec: 0.16, body: 185, bdec: 0.07, g: 0.5 }, hat: { f: 8000, dec: 0.035, open: 0.22, g: 0.2 } },
  taiko: { kick: { hi: 105, lo: 52, pdec: 0.07, dec: 0.55, g: 1.1 }, tom: { hi: 150, lo: 85, pdec: 0.1, dec: 0.5, g: 0.8 },
    snare: { f: 2600, q: 2, dec: 0.05, body: 330, bdec: 0.03, g: 0.38 }, hat: { f: 6000, dec: 0.06, open: 0.15, g: 0.18 } },
  frame: { kick: { hi: 100, lo: 62, pdec: 0.05, dec: 0.38, g: 0.95 }, tom: { hi: 220, lo: 160, pdec: 0.04, dec: 0.18, g: 0.45 },
    snare: { f: 3200, q: 1.6, dec: 0.05, body: 420, bdec: 0.025, g: 0.42 }, hat: { f: 9000, dec: 0.03, open: 0.12, g: 0.16 } },
  soft: { kick: { hi: 120, lo: 50, pdec: 0.04, dec: 0.22, g: 0.75 }, tom: { hi: 140, lo: 95, pdec: 0.08, dec: 0.3, g: 0.5 },
    snare: { f: 4200, q: 0.5, dec: 0.14, body: 0, bdec: 0, g: 0.28 }, hat: { f: 7000, dec: 0.06, open: 0.18, g: 0.16 } },
};

class Drums {
  private kick: { o: OscillatorNode; a: GainNode }; private tom: { o: OscillatorNode; a: GainNode };
  private sn: GainNode; private body: { o: OscillatorNode; a: GainNode } | null = null; private hat: GainNode;
  constructor(kit: Kit, private k: KitDef, out: AudioNode) {
    const tone = (v: KitVoice) => { const o = kit.osc('sine', v.lo), a = kit.gain(0); o.connect(a).connect(out); return { o, a }; };
    this.kick = tone(k.kick); this.tom = tone(k.tom);
    this.sn = kit.gain(0); kit.noise('white').connect(kit.filter('bandpass', k.snare.f, k.snare.q)).connect(this.sn).connect(out);
    if (k.snare.body) { const o = kit.osc('triangle', k.snare.body), a = kit.gain(0); o.connect(a).connect(out); this.body = { o, a }; }
    this.hat = kit.gain(0); kit.noise('white').connect(kit.filter('highpass', k.hat.f, 0.7)).connect(this.hat).connect(out);
  }
  private tonal(v: { o: OscillatorNode; a: GainNode }, d: KitVoice, t: number, vel: number, long: boolean): void {
    v.o.frequency.setValueAtTime(d.hi, t); v.o.frequency.setTargetAtTime(d.lo, t, tc(d.pdec));
    v.a.gain.cancelScheduledValues(t); v.a.gain.setTargetAtTime(d.g * vel, t, 0.0008); v.a.gain.setTargetAtTime(0, t + 0.006, tc(d.dec * (long ? 1.8 : 1)));
  }
  hit(which: 'kick' | 'snare' | 'hat' | 'tom', t: number, vel: number, open: boolean, gain: number): void {
    const k = this.k, v = vel * gain;
    if (which === 'kick') this.tonal(this.kick, k.kick, t, v, open);
    else if (which === 'tom') this.tonal(this.tom, k.tom, t, v, open);
    else if (which === 'snare') {
      const a = this.sn.gain; a.cancelScheduledValues(t); a.setTargetAtTime(k.snare.g * v, t, 0.0008); a.setTargetAtTime(0, t + 0.004, tc(k.snare.dec));
      if (this.body) { const b = this.body.a.gain; b.cancelScheduledValues(t); b.setTargetAtTime(k.snare.g * v * 0.8, t, 0.0008); b.setTargetAtTime(0, t + 0.004, tc(k.snare.bdec)); }
    } else {
      const a = this.hat.gain; a.cancelScheduledValues(t); a.setTargetAtTime(k.hat.g * v, t, 0.0006); a.setTargetAtTime(0, t + 0.003, tc(open ? k.hat.open : k.hat.dec));
    }
  }
}

const VEL: Record<string, number> = { X: 1, x: 0.75, g: 0.35, o: 0.8 };
const DRUM_KEYS = ['kick', 'snare', 'hat', 'tom'] as const;

export class Score {
  private stepDur: number; private nextT: number; private i = 0;
  private pad: Pad | null = null; private bass: Mono; private arp: Mono | null = null; private lead: Mono | null = null; private drums: Drums | null = null;
  private padN = 0; private padNotes = new Float64Array(4); private padCenter: number;
  private chordNotes = new Float64Array(3); private chordDeg = 0;
  private hot = false; private intensity = 0;
  // lead plan for the current phrase: absolute step, midi, length in steps
  private plan = new Float64Array(96); private planN = 0; private planUntil = -1;
  private pats: DrumPatterns = {};
  private breakdown = false; private intro = true;
  /** QA */
  notes = 0;

  constructor(private kit: Kit, private s: ScoreSpec, out: AudioNode) {
    const r = kit.rand, ac = kit.ac;
    this.stepDur = s.steps === 12 ? 60 / s.bpm / 3 : 60 / s.bpm / 4;
    const beat = this.stepDur * s.echo.steps;
    const send = kit.echo({ l: beat, r: Math.min(1.9, beat * 1.5), fb: s.echo.fb, damp: s.echo.damp, wet: s.echo.wet }, out);
    if (s.drone) {
      const f = kit.filter('lowpass', s.drone.cutoff, 0.7), g = kit.gain(0);
      for (const n of s.drone.notes) kit.osc(s.drone.wave, midiHz(s.root + n + 12 * s.drone.octave), (r() - 0.5) * 8).connect(f);
      f.connect(g).connect(out); g.gain.setTargetAtTime(s.drone.gain, ac.currentTime, 1.5);
    }
    if (s.pad) {
      this.padN = s.pad.voices; this.pad = new Pad(kit, this.padN, s.pad.wave, s.pad.detune, s.pad.cutoff, s.pad.q ?? 0.7, out);
      if (s.pad.send) { const g = kit.gain(s.pad.send); this.pad.a.connect(g).connect(send); }
    }
    this.padCenter = s.root + 12 * (s.pad?.octave ?? 1) + 5;
    this.bass = new Mono(kit, s.bass, out, send);
    if (s.arp) this.arp = new Mono(kit, s.arp, out, send);
    if (s.lead) this.lead = new Mono(kit, s.lead, out, send);
    if (s.drums) this.drums = new Drums(kit, KITS[s.drums.kit], out);
    this.nextT = ac.currentTime + 0.12;
  }

  setIntensity(x: number): void { this.intensity = x; }

  pump(now: number, horizon: number): void {
    while (this.nextT < horizon) {
      if (this.nextT < now - 0.03) { // the timer stalled (tab hitch): skip what is already late instead of piling it up
        this.i++; this.nextT += this.stepDur; continue;
      }
      this.step(this.i, this.nextT); this.i++; this.nextT += this.stepDur;
    }
  }

  private noteOf(deg: number): number { return degreeNote(this.s.root, this.s.mode, deg); }

  private barStart(bar: number, t: number): void {
    const s = this.s, prog = s.progression;
    this.chordDeg = prog[bar % prog.length];
    for (let k = 0; k < 3; k++) this.chordNotes[k] = this.noteOf(this.chordDeg + 2 * k);
    this.hot = this.intensity >= 0.5;
    this.intro = bar < 2;
    this.breakdown = !this.hot && bar % 32 >= 28;
    if (this.pad && s.pad) {
      // voice the chord near the pad centre (triad, plus the 7th for 4 voices); sorted so voices move by small steps
      for (let k = 0; k < this.padN; k++) {
        let m = this.noteOf(this.chordDeg + 2 * k);
        while (m < this.padCenter - 6) m += 12; while (m >= this.padCenter + 6) m -= 12;
        this.padNotes[k] = m;
      }
      const pn = this.padNotes.subarray(0, this.padN); pn.sort();
      this.pad.chord(t, pn, this.padN);
      const lvl = s.pad.gain * (this.hot ? 1.1 : 0.85 + 0.15 * Math.sin(bar * 0.9));
      this.pad.a.gain.setTargetAtTime(lvl, t, bar === 0 ? 1.2 : 0.6);
      this.pad.f.frequency.setTargetAtTime(s.pad.cutoff * (this.hot ? 1.6 : 1), t, 1.2);
    }
    // drum patterns for this bar
    const d = s.drums;
    if (d) {
      const base = this.hot && d.hot ? d.hot : d.main, fill = bar % 8 === 7 && d.fill ? d.fill : null;
      this.pats.kick = fill?.kick ?? base.kick; this.pats.snare = fill?.snare ?? base.snare; this.pats.hat = fill?.hat ?? base.hat; this.pats.tom = fill?.tom ?? base.tom;
    }
    // lead: start a motif on its bars (a motif may run over two bars)
    const L = s.lead;
    if (L && !this.intro && L.bars.includes(bar % 8) && bar * s.steps >= this.planUntil) {
      const r = this.kit.rand, mi = (L.bars.indexOf(bar % 8) + Math.floor(bar / 8)) % L.motifs.length, mo = L.motifs[mi].notes;
      let at = bar * s.steps; this.planN = 0;
      for (let k = 0; k < mo.length && this.planN < 32; k++) {
        const [deg, len] = mo[k];
        if (deg !== null && !(this.breakdown && k % 2 === 1)) {
          // light variation: an inner note sometimes steps to a neighbour
          const v = k > 0 && k < mo.length - 1 && r() < 0.18 ? (r() < 0.5 ? -1 : 1) : 0;
          const p = this.planN * 3; this.plan[p] = at; this.plan[p + 1] = this.noteOf(this.chordDeg + deg + v) + 12 * L.octave; this.plan[p + 2] = len; this.planN++;
        }
        at += len;
      }
      this.planUntil = at;
    }
  }

  private step(i: number, t: number): void {
    const s = this.s, n = s.steps, st = i % n, bar = Math.floor(i / n);
    if (st === 0) this.barStart(bar, t);
    const tt = st % 2 === 1 && s.swing ? t + s.swing * this.stepDur : t;
    // bass
    const bp = this.hot && s.bass.hot ? s.bass.hot : s.bass.pattern, bc = bp[st];
    if (bc !== '.' && bc !== '-') {
      let len = 1; while (st + len < n && bp[st + len] === '-') len++;
      const off = bc === '1' ? 0 : bc === '3' ? 2 : bc === '5' ? 4 : 7;
      this.bass.note(tt, this.noteOf(this.chordDeg + off) + 12 * s.bass.octave, len * this.stepDur * (s.bass.legato ?? 0.9), 0.85 + (st % 4 === 0 ? 0.15 : 0)); this.notes++;
    }
    if (this.intro) return;
    // arp
    if (this.arp && s.arp) {
      const ap = this.hot && s.arp.hot ? s.arp.hot : s.arp.pattern, k = ap[st];
      if (k >= 0) { const o = Math.floor(k / 3); this.arp.note(tt, this.chordNotes[k % 3] + 12 * (o + s.arp.octave), this.stepDur * (s.arp.legato ?? 0.9), st % 4 === 0 ? 1 : 0.8); this.notes++; }
    }
    // lead
    if (this.lead && s.lead) for (let k = 0; k < this.planN; k++) {
      const p = k * 3; if (this.plan[p] !== i) continue;
      this.lead.note(tt, this.plan[p + 1], this.plan[p + 2] * this.stepDur * (s.lead.legato ?? 0.9), 0.9); this.notes++;
    }
    // drums
    if (this.drums && s.drums) for (const key of DRUM_KEYS) {
      if (this.breakdown && key !== 'hat') continue;
      const c = this.pats[key]?.[st]; if (!c || c === '.') continue;
      this.drums.hit(key, tt, VEL[c] ?? 0.75, c === 'o', s.drums.gain); this.notes++;
    }
  }
}
